import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { SubscriptionStatusResponse } from "@/types/common"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Get plan lifecycle information from subscription status.
 * Handles both legacy (cancel_at_period_end) and newer (cancel_at timestamp) Stripe models.
 */
export function getPlanLifecycle(
  subscription: SubscriptionStatusResponse | null | undefined
): {
  statusLabel: string;
  isCanceling: boolean;
  renewsOn: string | null;
  cancelsOn: string | null;
} {
  if (!subscription) {
    return {
      statusLabel: 'Free',
      isCanceling: false,
      renewsOn: null,
      cancelsOn: null,
    };
  }

  const status = subscription.status?.toLowerCase() || '';
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  
  // Check if subscription is scheduled to cancel
  // Backend now correctly sets cancel_at_period_end based on cancel_at == current_period_end
  const isCanceling = subscription.cancel_at_period_end === true;
  
  // Don't show date for past_due status (payment issue needs to be resolved first)
  const isPastDue = status === 'past_due';
  
  const periodEnd = subscription.current_period_end;
  const renewsOn = !isCanceling && !isPastDue && periodEnd ? periodEnd : null;
  const cancelsOn = isCanceling && periodEnd ? periodEnd : null;

  return {
    statusLabel,
    isCanceling,
    renewsOn,
    cancelsOn,
  };
}

/**
 * Format date for display in billing UI.
 */
export function formatBillingDate(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}
